import React, { useState } from "react";
import { CategoryChip, PinnedChip, ClosedChip } from "./Chips";
import Markdown from "./Markdown";
import ReactionBar from "./ReactionBar";
import PollCard from "./PollCard";
import { whatsNewService } from "../../services/whatsNewService";

function formatDate(iso, withYear = true) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(
    "en-GB",
    withYear
      ? { day: "numeric", month: "short", year: "numeric" }
      : { day: "numeric", month: "short" },
  );
}

// Date: "9 Jul 2026" on desktop, short "9 Jul" on mobile (design 1f).
const PostDate = ({ iso }) => (
  <span className="text-xs sm:text-[12.5px] font-medium text-[#93A1B4] whitespace-nowrap">
    <span className="sm:hidden">{formatDate(iso, false)}</span>
    <span className="hidden sm:inline">{formatDate(iso)}</span>
  </span>
);

// A single feed card (design 1a desktop / 1f mobile). Renders a platform
// update or a poll. Bodies are Markdown; long bodies collapse to their first
// block with an inline "Read more". Reactions live on update posts; poll
// posts show the poll only.
const PostCard = ({ post }) => {
  const [reactions, setReactions] = useState(post.reactions || null);
  const [poll, setPoll] = useState(post.poll || null);
  const [reactBusy, setReactBusy] = useState(false);
  const [pollBusy, setPollBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [imgOk, setImgOk] = useState(true);

  const isPoll = Boolean(poll);
  const closedPoll = isPoll && Boolean(poll.is_closed);

  // Split the body into blocks so a long post shows its first block and reveals
  // the rest on demand (design's IMPROVEMENT card). Short posts show in full.
  const body = post.body || "";
  const blocks = body.trim().split(/\n\s*\n/);
  const hasMore = blocks.length > 1;
  const shownBody = expanded || !hasMore ? body : blocks[0];

  const handleReact = async (key) => {
    if (reactBusy || !reactions) return;
    const prev = reactions;
    const counts = { ...(prev.counts || {}) };
    const mine = prev.my_reaction || null;
    if (mine) counts[mine] = Math.max(0, (counts[mine] || 0) - 1);
    let nextMine;
    if (mine === key) {
      nextMine = null;
    } else {
      counts[key] = (counts[key] || 0) + 1;
      nextMine = key;
    }
    setReactions({ counts, my_reaction: nextMine });
    setReactBusy(true);
    const res = await whatsNewService.react(post.id, key);
    setReactBusy(false);
    if (res.success && res.reactions) setReactions(res.reactions);
    else setReactions(prev); // revert on failure
  };

  const handleVote = async (optionId) => {
    if (pollBusy) return;
    setPollBusy(true);
    const res = await whatsNewService.vote(post.id, optionId);
    setPollBusy(false);
    if (res.success && res.poll) setPoll(res.poll);
  };

  const heroShown = Boolean(post.image_url && imgOk);

  return (
    <article className="bg-white border border-[#E7EBF1] rounded-2xl shadow-[0_1px_2px_rgba(16,27,44,0.04)] p-4 sm:px-6 sm:py-[22px]">
      {/* Header: category chip, optional closed/pinned markers, date */}
      <div className="flex items-center gap-1.5 sm:gap-2">
        <CategoryChip category={post.category} />
        {closedPoll && <ClosedChip />}
        {post.is_pinned ? (
          <>
            <div className="ml-auto">
              <PinnedChip />
            </div>
            <PostDate iso={post.published_at} />
          </>
        ) : (
          <div className="ml-auto">
            <PostDate iso={post.published_at} />
          </div>
        )}
      </div>

      {/* Optional hero image — never cropped (object-contain, capped height). */}
      {heroShown && (
        <div className="mt-3 sm:mt-4 rounded-[11px] sm:rounded-xl overflow-hidden bg-[#EAF0F7]">
          <img
            src={post.image_url}
            alt=""
            onError={() => setImgOk(false)}
            className="block w-full h-auto max-h-[320px] object-contain mx-auto"
          />
        </div>
      )}

      <h3
        className={[
          heroShown ? "mt-3 sm:mt-4" : "mt-[11px] sm:mt-3.5",
          "text-base sm:text-[17.5px] font-bold text-[#101B2C] leading-[1.35] tracking-[-0.01em]",
        ].join(" ")}
      >
        {post.title}
      </h3>

      {isPoll ? (
        <PollCard poll={poll} onVote={handleVote} busy={pollBusy} />
      ) : (
        <>
          {body && (
            <div className="mt-1.5 sm:mt-2">
              <Markdown>{shownBody}</Markdown>
              {hasMore && (
                <button
                  type="button"
                  onClick={() => setExpanded((v) => !v)}
                  className="mt-[9px] sm:mt-2.5 text-[13.5px] sm:text-[13px] font-bold text-[#2283FF] hover:text-[#1668D9]"
                >
                  {expanded ? "Show less" : "Read more"}
                </button>
              )}
            </div>
          )}
          {reactions && (
            <ReactionBar reactions={reactions} onReact={handleReact} disabled={reactBusy} />
          )}
        </>
      )}
    </article>
  );
};

export default PostCard;
